import React from 'react';
import PageLayout from '../../components/PageLayout';
import Card from '../../components/ui/Card';
import Icon from '../../components/AppIcon';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, PieChart, Pie, Cell, Legend
} from 'recharts';

const dataVolunteerGrowth = [
    { name: 'Jan', volunteers: 400 },
    { name: 'Feb', volunteers: 550 },
    { name: 'Mar', volunteers: 700 },
    { name: 'Apr', volunteers: 900 },
    { name: 'May', volunteers: 1100 },
    { name: 'Jun', volunteers: 1350 },
];

const dataHoursByCategory = [
    { name: 'Education', value: 4500 },
    { name: 'Environment', value: 3200 },
    { name: 'Health', value: 2100 },
    { name: 'Community', value: 5600 },
];

const dataEventsPerMonth = [
    { name: 'Jan', events: 4 },
    { name: 'Feb', events: 6 },
    { name: 'Mar', events: 8 },
    { name: 'Apr', events: 12 },
    { name: 'May', events: 9 },
    { name: 'Jun', events: 15 },
];

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#8b5cf6'];

const SystemReports = () => {
    const handleExport = () => {
        alert("Report generated! Downloading CSV...");
        // In a real app, this would process the data arrays into a CSV
        const headers = ["Metric,Value"];
        const rows = [
            ...dataVolunteerGrowth.map(d => `Volunteer Growth (${d.name}),${d.volunteers}`),
            ...dataHoursByCategory.map(d => `Hours (${d.name}),${d.value}`),
            ...dataEventsPerMonth.map(d => `Events (${d.name}),${d.events}`)
        ];

        const csvContent = "data:text/csv;charset=utf-8," + [headers, ...rows].join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "system_reports_summary.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <PageLayout variant="dark">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-indigo-500/20 rounded-lg">
                            <Icon name="BarChart2" size={24} className="text-indigo-400" />
                        </div>
                        <h1 className="text-3xl font-bold text-white tracking-tight">System Reports</h1>
                    </div>
                    <p className="text-slate-400">Comprehensive analytics and platform performance metrics</p>
                </div>
                <button
                    onClick={handleExport}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                >
                    <Icon name="Download" size={18} />
                    <span>Export CSV</span>
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {/* Volunteer Growth Chart */}
                <Card className="bg-slate-800 border-slate-700 p-6">
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                        <Icon name="TrendingUp" size={20} className="text-green-400" />
                        Volunteer Growth
                    </h3>
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={dataVolunteerGrowth}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                <XAxis dataKey="name" stroke="#94a3b8" />
                                <YAxis stroke="#94a3b8" />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f8fafc' }}
                                    itemStyle={{ color: '#f8fafc' }}
                                />
                                <Line type="monotone" dataKey="volunteers" stroke="#6366f1" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 8 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                {/* Hours by Category Pie Chart */}
                <Card className="bg-slate-800 border-slate-700 p-6">
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                        <Icon name="PieChart" size={20} className="text-purple-400" />
                        Impact Distribution (Hours)
                    </h3>
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={dataHoursByCategory}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    fill="#8884d8"
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {dataHoursByCategory.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f8fafc' }}
                                    itemStyle={{ color: '#f8fafc' }}
                                />
                                <Legend wrapperStyle={{ color: '#94a3b8' }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            </div>

            <div className="grid grid-cols-1 gap-8">
                {/* Events Per Month Bar Chart */}
                <Card className="bg-slate-800 border-slate-700 p-6">
                    <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                        <Icon name="Calendar" size={20} className="text-orange-400" />
                        Events Frequency
                    </h3>
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={dataEventsPerMonth}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                <XAxis dataKey="name" stroke="#94a3b8" />
                                <YAxis stroke="#94a3b8" />
                                <Tooltip
                                    contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#f8fafc' }}
                                    itemStyle={{ color: '#f8fafc' }}
                                />
                                <Bar dataKey="events" fill="#10b981" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            </div>

        </PageLayout>
    );
};

export default SystemReports;
